"use client";

import { useEffect, useState } from "react";
import {
  Crown,
  Gift,
  Package,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
} from "lucide-react";

/**
 * THE SUBSCRIPTION MOTIF — six icons cycling on a vertical carousel.
 *
 * Extracted from `SubscriptionsNavItem`, which owned it inline. The banner on the
 * subscriptions page needed the same animation, and the alternative was a second
 * copy of the icon list, the interval and the keyframes — three things that would
 * have drifted the first time anyone added an icon.
 *
 * ONLY THE ICON ANIMATION lives here. The nav pill's rotating conic-gradient
 * border stays in that component: it is the pill's own treatment, not the motif's,
 * and putting it on a full-width banner would trace a light around a rectangle
 * the size of the page.
 *
 * The keyframes are in `globals.css` under `.gl-sub-nav-icon` for the same
 * reason — two renderers, one definition.
 *
 * `color: currentColor` on the icon, so a caller sets the colour by setting its
 * own text colour and this never has to know whether it sits on coral or cream.
 */

const ICONS = [Crown, Gift, ShoppingBag, Package, Sparkles, ShieldCheck];
const ICON_INTERVAL_MS = 3500;

interface RotatingSubscriptionIconProps {
  /** Rendered box AND icon size, in px. */
  size?: number;
  strokeWidth?: number;
}

export function RotatingSubscriptionIcon({
  size = 16,
  strokeWidth = 1.9,
}: RotatingSubscriptionIconProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ICONS.length),
      ICON_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, []);

  const previousIndex = (index - 1 + ICONS.length) % ICONS.length;
  const CurrentIcon = ICONS[index];
  const PreviousIcon = ICONS[previousIndex];

  return (
    <span
      className="gl-sub-nav-icons"
      aria-hidden="true"
      style={{ width: size, height: size }}
    >
      {/* The outgoing icon is omitted on the very first render: there is nothing
          to animate away from, and rendering one would slide a Crown up and out
          the moment the page painted. */}
      {index > 0 && (
        <span key={`out-${previousIndex}`} className="gl-sub-nav-icon out">
          <PreviousIcon size={size} strokeWidth={strokeWidth} />
        </span>
      )}
      <span key={`in-${index}`} className="gl-sub-nav-icon in">
        <CurrentIcon size={size} strokeWidth={strokeWidth} />
      </span>
    </span>
  );
}

export default RotatingSubscriptionIcon;
